/**
 * Timeline of the edition player (NEWS-03/04/05/12): which slide is on, how
 * long it has played, pause (button/Space) and hold (long press). Pure; the
 * player drives `tick` from a rAF loop (never under reduced motion, NEWS-09).
 */

export interface PlayerState {
  /** Duration of each slide; `null` = never advances (the outro). */
  durations: ReadonlyArray<number | null>;
  index: number;
  elapsedMs: number;
  paused: boolean;
  held: boolean;
  /** Bumped on every slide change, so the slide's animations restart. */
  visit: number;
}

export type PlayerAction =
  | { type: 'go'; index: number }
  | { type: 'next' }
  | { type: 'prev' }
  | { type: 'tick'; dtMs: number }
  | { type: 'togglePause' }
  | { type: 'hold'; held: boolean }
  | { type: 'restart' };

function clampIndex(state: PlayerState, index: number): number {
  return Math.max(0, Math.min(state.durations.length - 1, index));
}

function enter(state: PlayerState, index: number): PlayerState {
  return {
    ...state,
    index: clampIndex(state, index),
    elapsedMs: 0,
    visit: state.visit + 1,
  };
}

export function createPlayerState(
  durations: ReadonlyArray<number | null>,
  startIndex = 0
): PlayerState {
  const state: PlayerState = {
    durations,
    index: 0,
    elapsedMs: 0,
    paused: false,
    held: false,
    visit: 0,
  };
  return { ...state, index: clampIndex(state, startIndex) };
}

export function playerReducer(
  state: PlayerState,
  action: PlayerAction
): PlayerState {
  switch (action.type) {
    case 'go':
      return enter(state, action.index);
    case 'next':
      // Clamped at the ends: the last slide stays as it is.
      return state.index >= state.durations.length - 1
        ? state
        : enter(state, state.index + 1);
    case 'prev':
      return state.index <= 0 ? state : enter(state, state.index - 1);
    case 'restart':
      return enter(state, 0);
    case 'togglePause':
      return { ...state, paused: !state.paused };
    case 'hold':
      return { ...state, held: action.held };
    case 'tick': {
      const duration = state.durations[state.index] ?? null;
      if (state.paused || state.held || duration === null) return state;
      const elapsedMs = state.elapsedMs + action.dtMs;
      if (elapsedMs < duration) return { ...state, elapsedMs };
      return state.index < state.durations.length - 1
        ? enter(state, state.index + 1)
        : { ...state, elapsedMs: duration };
    }
  }
}

/**
 * Fill of bar `barIndex` in 0–1: earlier slides full, later ones empty, the
 * current one `elapsed / duration` (full when it has no duration).
 */
export function barProgress(state: PlayerState, barIndex: number): number {
  if (barIndex < state.index) return 1;
  if (barIndex > state.index) return 0;
  const duration = state.durations[state.index] ?? null;
  if (duration === null) return 1;
  return Math.min(1, state.elapsedMs / duration);
}
